import { useCallback, useState } from 'react';
import { API_CORE } from '../../../../../_CORE/API/ApiCORE';
import { notify } from '../../../../../_CORE/APP/Templates/_UI/Alerts/AppAlertHost';
import { attachSecurityToken, useActionVerification } from './useActionVerification';

const normalizeError = (response, fallback = 'No se pudo completar la operacion.') => {
    const status = response?.status ?? response?.code_status ?? 500;
    if (status >= 400 || response?.success === false) {
        throw new Error(response?.message || fallback);
    }
    return response?.data ?? response;
};

export const usePortfolioAdmin = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const { authorize } = useActionVerification();

    const resolveEndpoint = useCallback((endpointKey) => {
        const endpoint = API_CORE.ENDPOINTS[endpointKey];
        if (!endpoint) throw new Error(`Endpoint no registrado: ${endpointKey}`);
        return endpoint;
    }, []);

    const get = useCallback(async (endpointKey) => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await API_CORE.SERVICES.GET_RESOURCE(resolveEndpoint(endpointKey));
            return normalizeError(response, 'No se pudieron cargar los datos.');
        } catch (err) {
            setError(err);
            throw err;
        } finally {
            setIsLoading(false);
        }
    }, [resolveEndpoint]);

    const post = useCallback(async (endpointKey, data, config = {}) => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await API_CORE.SERVICES.POST_RESOURCE(resolveEndpoint(endpointKey), data, config);
            return normalizeError(response, 'No se pudieron guardar los cambios.');
        } catch (err) {
            setError(err);
            throw err;
        } finally {
            setIsLoading(false);
        }
    }, [resolveEndpoint]);

    const securePost = useCallback(async (endpointKey, data, config = {}, options = {}) => {
        setIsLoading(true);
        setError(null);
        try {
            const endpoint = resolveEndpoint(endpointKey);
            const token = await authorize(endpoint);
            const payload = attachSecurityToken(data, token);
            const response = await API_CORE.SERVICES.POST_RESOURCE(endpoint, payload, config);
            const result = normalizeError(response, options.errorMessage || 'No se pudo completar la operacion.');
            if (endpoint.startsWith('/portfolio/')) {
                try { window.localStorage.setItem('portfolio:last-update', String(Date.now())); } catch (_) { /* storage opcional */ }
            }
            notify.success(options.successTitle || response?.message || 'Accion completada', options.successText || 'Los cambios se guardaron correctamente.');
            return result;
        } catch (err) {
            setError(err);
            if (!err?.cancelled && !err?.notified) notify.error(options.errorTitle || 'No se pudo completar', err?.message || options.errorMessage || 'Ocurrio un error.');
            throw err;
        } finally {
            setIsLoading(false);
        }
    }, [authorize, resolveEndpoint]);

    return { get, post, securePost, isLoading, error };
};
